import {
  AddCircle,
  AppRegistration,
  Home,
  Settings,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import ReusableTable from "../../../components/custom/data_table/ReusableTable";
import IconBreadcrumbs from "../../../components/custom/IconBreadcrumbs";
import Loader from "../../../components/custom/Loader";
import {
  useDeleteRegistrationSystemsMutation,
  useGetRegistrationSystemsQuery,
} from "../../../redux/api/registrationSystemsApiSlice";
import ShowFilePreviewModal from "../../frontend/userPanel/form_two/ShowPreviewFiles";
import AddData from "./AddData";
import { convertToBanglaNumerals } from "../../../utils/convertToBanglaNumerals";

const RegistrationSystems = () => {
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [type, setType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data, isLoading, refetch } = useGetRegistrationSystemsQuery({
    currentPage,
    searchValue,
    type,
  });
  const [
    deleteData,
    {
      isLoading: isLoadingDelete,
      isError: isErrorDelete,
      error: errorDelete,
      isSuccess: isSuccessDelete,
      data: dataDelete,
    },
  ] = useDeleteRegistrationSystemsMutation();

  const totalData = data?.meta?.total;
  const lastPage = data?.meta?.last_page;

  const debouncedSearch = useCallback(
    debounce((value) => {
      setCurrentPage(1);
      setSearchValue(value);
      refetch();
    }, 400),
    []
  );

  useEffect(() => {
    // Cleanup function to cancel the debounce when the component unmounts or debouncedSearch changes
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    refetch();
  };
  // handle delete
  const handleDeleteData = (id) => {
    Swal.fire({
      title: "আপনি কি নিশ্চিত?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteData(id);
      }
    });
  };
  useEffect(() => {
    if (isSuccessDelete && dataDelete) {
      toast.success(dataDelete?.message);
    }
    if (isErrorDelete && errorDelete) {
      toast.error(errorDelete.message);
    }
  }, [
    isErrorDelete,
    isSuccessDelete,
    dataDelete,
    isLoadingDelete,
    errorDelete,
  ]);
  const columns = [
    { label: "ইনডেক্স", name: "index" },
    {
      label: "বর্ণনা",
      name: "description",
      options: {
        customBodyRender: (value) => {
          return value ? (
            <span
              className="m-1 py-1 px-3 border rounded"
              style={{ fontSize: "14px" }}
            >
              {value}
            </span>
          ) : (
            ""
          );
        },
      },
    },
    {
      label: "ক্যাটাগরি",
      name: "category",
    },

    { label: "টাইপ নাম", name: "typeName" },
    {
      label: "ডকুমেন্ট",
      name: "document",
      options: {
        customBodyRender: (value) => {
          return (
            value && (
              <Badge
                onClick={() => {
                  setPreviewModal(value ? true : false);
                  setPreviewFile(value);
                }}
                style={{ cursor: "pointer" }}
              >
                View
              </Badge>
            )
          );
        },
      },
    },
    {
      label: "স্ট্যাটাস",
      name: "status",
      options: {
        customBodyRender: (value) => {
          let status, color;

          switch (value) {
            case 1:
              status = "active";
              color = "success";
              break;
            case 2:
              status = "In-Active";
              color = "danger";
              break;
            default:
              status = null;
              color = null;
          }

          return <Badge bg={color}>{status}</Badge>;
        },
      },
    },
    {
      label: "ক্রিয়াকলাপ",
      name: "action",
      options: {
        customBodyRender: (value, tableMeta) => (
          <Box display={"flex"} gap={1}>
            <Tooltip title="Edit" placement="top" arrow>
              <Badge
                bg="warning"
                className="p-2"
                onClick={() => {
                  setEditData(tableMeta.rowData);
                  setShowModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                Edit
              </Badge>
            </Tooltip>
            <Tooltip title="Delete" placement="top" arrow>
              <Badge
                bg="danger"
                className="p-2"
                onClick={() => handleDeleteData(tableMeta.rowData?.id)}
                style={{ cursor: "pointer" }}
              >
                Delete
              </Badge>
            </Tooltip>
          </Box>
        ),
      },
    },
  ];

  const dataTable = data?.data.map((item, i) => ({
    id: item.id,
    description: item.description,
    category: item?.category?.name || "",
    category_id: item?.category?.id || "",
    typeName: item.typeName,
    type: item.type,
    index: convertToBanglaNumerals(item.sorting_index),
    sorting_index: item.sorting_index,
    status: item.status,
    document: item.document,
  }));

  const breadcrumbsData = [
    { icon: Home, title: "হোম", url: "/" },
    { icon: Settings, title: "সেটিংস" },
    {
      icon: AppRegistration,
      title: "রেজিস্ট্রেশনের নিয়মাবলী",
      active: true,
    },
  ];

  const handleSelectChange = (event) => {
    const selectedValue = event.target.value;
    setType(selectedValue);
    setCurrentPage(1);
    refetch();
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        py={1}
        mb={1}
      >
        <IconBreadcrumbs breadcrumbs={breadcrumbsData} />
        <Button
          variant="outline-secondary"
          onClick={() => {
            setEditData(null);
            setShowModal(true);
          }}
        >
          <AddCircle /> রেজিস্ট্রেশনের নিয়মাবলী যোগ করুন
        </Button>
      </Box>
      <div className="slideUp dashboard-layout p-4">
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          }}
          className="rounded mb-3 p-2"
        >
          <div className="row">
            {/* <div className="col-md-8"></div> */}
            <div className="col-md-4 ">
              <Form.Select
                value={type}
                onChange={handleSelectChange}
                aria-label="Default select example"
              >
                <option value="">ধরন নির্বাচন করুন</option>
                <option value="1">রেজিস্ট্রেশনের নিয়মাবলী</option>
                <option value="2">অন্যান্য দলিলপত্র</option>
              </Form.Select>
            </div>
          </div>
        </div>
        <ReusableTable
          title={"রেজিস্ট্রেশনের নিয়মাবলী"}
          columns={columns}
          data={dataTable}
          isLoading={isLoading}
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={totalData}
          onSearchChange={handleSearchChange}
          searchText={searchText}
          onPageChange={handlePageChange}
        />
      </div>
      {showModal && (
        <AddData
          editData={editData}
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setEditData(null);
          }}
        />
      )}

      <ShowFilePreviewModal
        setPreviewModal={setPreviewModal}
        previewModal={previewModal}
        previewUrl={previewFile}
        setPreviewUrl={setPreviewFile}
      />
    </div>
  );
};

export default RegistrationSystems;
